import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import SiteLayout from '@/components/feature/SiteLayout'
import MovieCard from '@/components/feature/MovieCard'
import SectionHeading from '@/components/feature/SectionHeading'
import { useFavorites } from '@/hooks/useFavorites'
import { usePageSeo } from '@/hooks/usePageSeo'
import { categories, movies } from '@/mocks/movies'
import PlayerDemo from './components/PlayerDemo'
import ShareDialog from './components/ShareDialog'

const categoryLabel: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  short: '短片',
}

export default function Detail() {
  const { id } = useParams<{ id: string }>()
  const movie = movies.find((item) => item.id === id)
  const [shareOpen, setShareOpen] = useState(false)
  const { isFavorite, toggleFavorite } = useFavorites()

  usePageSeo(
    movie ? `${movie.title} - 麻豆传媒在线影视详情` : '作品不存在 - 麻豆传媒在线',
    movie
      ? `${movie.title}（${movie.year}）${categoryLabel[movie.category]}，导演${movie.director}，主演${movie.cast.join('、')}。剧情简介：${movie.synopsis.slice(0, 60)}`
      : '未找到对应的影视作品，可返回分类页浏览全部示例作品。',
    movie
      ? `${movie.title},${movie.genres.join(',')},麻豆传媒在线影视详情,${movie.year}影视作品`
      : '麻豆传媒在线,影视详情',
  )

  if (!movie) {
    return (
      <SiteLayout>
        <div className="page-shell flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-file-forbid-line text-2xl" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-950">没有找到这部作品</h1>
          <p className="mt-2 max-w-md text-sm leading-6 text-foreground-500">
            链接可能已经失效，或者作品已从当前示例数据中移除。可以回到分类页继续浏览其他影视作品。
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="whitespace-nowrap rounded-md bg-primary-800 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              浏览影视分类
            </Link>
            <Link
              to="/"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
            >
              返回首页
            </Link>
          </div>
        </div>
      </SiteLayout>
    )
  }

  const favorited = isFavorite(movie.id)
  const related = [
    ...movies.filter((item) => item.id !== movie.id && item.category === movie.category),
    ...movies.filter(
      (item) =>
        item.id !== movie.id &&
        item.category !== movie.category &&
        item.genres.some((genre) => movie.genres.includes(genre)),
    ),
  ].slice(0, 4)

  return (
    <SiteLayout>
      <div className="relative overflow-hidden bg-primary-950">
        <div className="absolute inset-0">
          <img
            src={movie.still}
            alt={`《${movie.title}》背景剧照`}
            title={`${movie.title} - 作品详情`}
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/70" />
        </div>

        <div className="page-shell relative py-6 md:py-10">
          <nav
            aria-label="面包屑导航"
            className="flex flex-wrap items-center gap-1.5 text-xs text-background-300"
          >
            <Link to="/" className="transition-colors hover:text-accent-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-background-500" />
            <Link to="/category" className="transition-colors hover:text-accent-300">
              影视分类
            </Link>
            <i className="ri-arrow-right-s-line text-background-500" />
            <Link
              to={`/category?type=${movie.category}`}
              className="transition-colors hover:text-accent-300"
            >
              {categoryLabel[movie.category]}
            </Link>
            <i className="ri-arrow-right-s-line text-background-500" />
            <span className="text-background-100">{movie.title}</span>
          </nav>

          <div className="mt-5 grid grid-cols-1 gap-5 md:mt-7 md:grid-cols-12 md:gap-7">
            <div className="w-full max-w-[220px] md:col-span-3 md:max-w-none">
              <div className="overflow-hidden rounded-lg border border-background-50/15 bg-primary-900">
                <img
                  src={movie.poster}
                  alt={`《${movie.title}》高清封面`}
                  title={`${movie.title} - 麻豆传媒在线影视详情`}
                  className="aspect-[3/4] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="md:col-span-9">
              <div className="flex flex-wrap items-center gap-2 text-[11px]">
                <span className="rounded bg-accent-500 px-2 py-0.5 font-medium text-background-50">
                  {categoryLabel[movie.category]}
                </span>
                {movie.isNew && (
                  <span className="rounded border border-accent-400/50 px-2 py-0.5 text-accent-300">
                    新上线
                  </span>
                )}
                <span className="rounded border border-background-50/25 px-2 py-0.5 text-background-200">
                  高清演示
                </span>
              </div>

              <h1 className="mt-3 text-2xl font-bold leading-tight tracking-tight text-background-50 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-2 text-sm text-accent-200">{movie.tagline}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-background-300">
                <span className="font-label tracking-wide">{movie.year}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>{movie.duration}</span>
                <span className="h-3 w-px bg-background-50/25" />
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-[10px] text-accent-400" />
                  <span className="font-label text-sm text-background-50">
                    {movie.rating.toFixed(1)}
                  </span>
                  / 10
                </span>
                <span className="h-3 w-px bg-background-50/25" />
                <span>导演 {movie.director}</span>
              </div>

              <p className="mt-4 line-clamp-3 max-w-2xl text-sm leading-6 text-background-200">
                {movie.synopsis}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-2.5">
                <a
                  href="#player"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                >
                  <i className="ri-play-fill text-base" />
                  播放演示
                </a>
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  aria-pressed={favorited}
                  className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm transition-colors ${
                    favorited
                      ? 'border-accent-400 bg-accent-500/15 text-accent-200'
                      : 'border-background-50/30 text-background-100 hover:border-background-50/60'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-3-fill' : 'ri-heart-3-line'} />
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/30 px-4 py-2.5 text-sm text-background-100 transition-colors hover:border-background-50/60"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <span className="text-[11px] text-background-400">所属分类</span>
                {movie.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?type=${movie.category}&genre=${encodeURIComponent(genre)}`}
                    className="whitespace-nowrap rounded-full border border-background-50/20 px-2.5 py-1 text-[11px] text-background-200 transition-colors hover:border-accent-400 hover:text-accent-300"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-shell mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="flex flex-col gap-8 lg:col-span-8">
          <section aria-labelledby="synopsis-title">
            <h2 id="synopsis-title" className="text-lg font-bold tracking-tight text-foreground-950">
              剧情简介
            </h2>
            <div className="mt-3 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-background-200 pt-4 text-[11px] text-foreground-500">
                <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-900">
                  {categoryLabel[movie.category]}
                </span>
                {movie.genres.map((genre) => (
                  <span key={genre} className="rounded bg-background-100 px-2 py-0.5">
                    {genre}
                  </span>
                ))}
                <span className="ml-auto flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  {movie.year} 年
                </span>
              </div>
            </div>
          </section>

          <section id="player" aria-labelledby="player-title" className="scroll-mt-24">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 id="player-title" className="text-lg font-bold tracking-tight text-foreground-950">
                在线播放
              </h2>
              <span className="flex items-center gap-1 rounded-full bg-background-100 px-2.5 py-1 text-[10px] text-foreground-500">
                <i className="ri-shield-check-line text-accent-600" />
                演示模式 · 无真实片源
              </span>
            </div>
            <PlayerDemo still={movie.still} title={movie.title} />
          </section>

          <section aria-labelledby="credits-title">
            <h2 id="credits-title" className="text-lg font-bold tracking-tight text-foreground-950">
              演职人员
            </h2>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-[11px] tracking-wide text-foreground-500">导演</p>
                <p className="mt-1.5 text-sm font-medium text-foreground-950">{movie.director}</p>
              </div>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <p className="text-[11px] tracking-wide text-foreground-500">主要演员</p>
                <p className="mt-1.5 text-sm font-medium text-foreground-950">
                  {movie.cast.join(' · ')}
                </p>
              </div>
            </div>
          </section>
        </div>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="text-sm font-semibold text-foreground-950">作品信息</h2>
            <dl className="mt-4 space-y-3 text-xs">
              {[
                { label: '作品名称', value: movie.title },
                { label: '分类', value: categoryLabel[movie.category] },
                { label: '题材类型', value: movie.genres.join(' / ') },
                { label: '上映年份', value: `${movie.year} 年` },
                { label: '时长 / 集数', value: movie.duration },
                { label: '导演', value: movie.director },
                { label: '评分', value: `${movie.rating.toFixed(1)} / 10` },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4">
                  <dt className="shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-right text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-[11px] text-foreground-500">快速前往</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Link
                  to={`/category?type=${movie.category}`}
                  className="whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
                >
                  {categoryLabel[movie.category]}分类
                </Link>
                {categories
                  .filter((item) => item.key !== movie.category)
                  .slice(0, 2)
                  .map((item) => (
                    <Link
                      key={item.key}
                      to={`/category?type=${item.key}`}
                      className="whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-[11px] text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-800"
                    >
                      {item.label}分类
                    </Link>
                  ))}
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <p className="flex items-center gap-2 text-xs font-medium text-accent-900">
              <i className="ri-heart-3-line" />
              收藏可以在本机保留
            </p>
            <p className="mt-2 text-[11px] leading-5 text-accent-800">
              收藏状态保存在当前浏览器本地，刷新页面后依然保留，不会上传到服务器。
            </p>
            <button
              type="button"
              onClick={() => toggleFavorite(movie.id)}
              className="mt-3 w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-accent-600"
            >
              {favorited ? '取消收藏该作品' : '收藏该作品'}
            </button>
          </div>
        </aside>
      </div>

      <section className="page-shell mt-14" aria-labelledby="related-title">
        <div id="related-title">
          <SectionHeading
            eyebrow="YOU MAY ALSO LIKE"
            title="相关推荐"
            description="根据作品分类与题材类型生成的简单推荐，仅作浏览参考。"
            actionLabel="查看更多同类"
            actionTo={`/category?type=${movie.category}`}
          />
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
          {related.map((item) => (
            <MovieCard key={item.id} movie={item} />
          ))}
        </div>
      </section>

      <ShareDialog open={shareOpen} title={movie.title} onClose={() => setShareOpen(false)} />
    </SiteLayout>
  )
}